import * as PropTypes from 'prop-types';

import * as ArchiveConstants from 'archive/ArchiveConstants';
import {Permissions, PERMISSIONS_SHAPE} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {Concentration, LegacyMeasurement, MEASUREMENT_SHAPE} from 'constants/MeasurementConstants';
import ModelTypeConstants, {StorableItemType} from 'constants/ModelTypeConstants';
import {TagSchema, TagSchemaSummary} from 'constants/RegistryConstants';
import {
  CheckOutStatusInfo,
  SAMPLE_ROLES_SHAPE,
  SampleRestrictionStatus,
  SampleRoles,
  SampleStatusItem,
} from 'constants/SampleStatusConstants';
import {User, USER_SHAPE} from 'constants/UserConstants';
import * as FileConstants from 'folder-items/FileConstants';
import {AsyncValidationStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {EvaluatedCellError} from 'notebook/tables/excess/Evaluator';
import {ExperimentalRole} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {
  CONTAINER_SCHEMA_SHAPE,
  ContainerSchema,
  GRID_SCHEMA_SHAPE,
  GridSchema,
  PlateSchema,
  STORABLE_SCHEMA_SHAPE,
  StorableSchema,
  StorableSchemaSummary,
} from 'samples/storables/StorableSchemaConstants';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {SchemaFieldEdge} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {SchemaValidationErrorRecord, SchemaValidationState} from 'schemas/SchemaValidationConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {Optional} from 'util/Types';

import {BoxGraphQL} from '../../graphql-fragments/inventory/__generated/BoxGraphQL';
import {BoxSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/BoxSummaryGraphQL';
import {ContainerGraphQL} from '../../graphql-fragments/inventory/__generated/ContainerGraphQL';
import {GridGraphQL} from '../../graphql-fragments/inventory/__generated/GridGraphQL';
import {LocationSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/LocationSummaryGraphQL';
import {PlateGraphQL} from '../../graphql-fragments/inventory/__generated/PlateGraphQL';
import {PlateSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/PlateSummaryGraphQL';
import {StorableSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';
import {SourceSummary} from './SourceConstants';

type Tag = FileConstants.Tag;

// Keep in sync with `benchling/inventory/inventory_util.py`
export const MAX_SIGNIFICANT_FIGURES = 6;

export function getMaxSignificantFigures(): number {
  return BenchlingConfig.INVENTORY_ENABLE_HIGHER_PRECISION ? 15 : 6;
}

export interface BatchViewState {
  view: BatchViewType;
  batchId: string | null;
  containerId?: string | null;
  filter: string;
  createBatchModal: null;
}

export enum BatchViewType {
  VIEW_ALL = 'VIEW_ALL',
  VIEW = 'VIEW',
}

export const MODAL_PANE = {
  CHOOSE_TRANSFER_INPUT: 'CHOOSE_TRANSFER_INPUT',
  CHOOSE_TRANSFER_OUTPUT: 'CHOOSE_TRANSFER_OUTPUT',
  CONFIRM_CONCENTRATIONS: 'CONFIRM_CONCENTRATIONS',
  CONFIRM_SAMPLE_CONTROLS: 'CONFIRM_SAMPLE_CONTROLS',
  CONFIRM_TRANSFER_QUANTITIES: 'CONFIRM_TRANSFER_QUANTITIES',
  CREATE_BOX: 'CREATE_BOX',
  CREATE_CONTAINERS: 'CREATE_CONTAINERS',
  CREATE_LOCATION: 'CREATE_LOCATION',
  CREATE_PLATE: 'CREATE_PLATE',
  EXISTING_PLATE: 'EXISTING_PLATE',
  SELECT_PARENT_STORABLE: 'SELECT_PARENT_STORABLE',
  TRANSFER_PLATES_QUANTITY: 'TRANSFER_PLATES_QUANTITY',
};

export enum ModalPane {
  CHOOSE_TRANSFER_INPUT = 'CHOOSE_TRANSFER_INPUT',
  CHOOSE_TRANSFER_OUTPUT = 'CHOOSE_TRANSFER_OUTPUT',
  CONFIRM_CONCENTRATIONS = 'CONFIRM_CONCENTRATIONS',
  CONFIRM_SAMPLE_CONTROLS = 'CONFIRM_SAMPLE_CONTROLS',
  CONFIRM_TRANSFER_QUANTITIES = 'CONFIRM_TRANSFER_QUANTITIES',
  CREATE_BOX = 'CREATE_BOX',
  CREATE_CONTAINERS = 'CREATE_CONTAINERS',
  CREATE_LOCATION = 'CREATE_LOCATION',
  CREATE_PLATE = 'CREATE_PLATE',
  EXISTING_PLATE = 'EXISTING_PLATE',
  SELECT_PARENT_STORABLE = 'SELECT_PARENT_STORABLE',
  TRANSFER_PLATES_QUANTITY = 'TRANSFER_PLATES_QUANTITY',
}

export enum TransferFlow {
  TRANSFER_IN = 'TRANSFER_IN',
  TRANSFER_OUT = 'TRANSFER_OUT',
  TRANSFER_GLOBAL = 'TRANSFER_GLOBAL',
}

export const PARENT_STORABLE_PANES = [
  MODAL_PANE.CREATE_BOX,
  MODAL_PANE.CREATE_CONTAINERS,
  MODAL_PANE.CREATE_LOCATION,
  MODAL_PANE.CREATE_PLATE,
  MODAL_PANE.SELECT_PARENT_STORABLE,
];

export type CreateStorablePanes =
  | ModalPane.CREATE_BOX
  | ModalPane.CREATE_CONTAINERS
  | ModalPane.CREATE_LOCATION
  | ModalPane.CREATE_PLATE;

// Keep in sync with SpreadsheetFormat enum in benchling/inventory/storables/grids_util.py
export const UPLOAD_CONTAINERS_SPREADSHEET_FORMAT = {
  ONE_CONTAINER_PER_ROW: 'ONE_CONTAINER_PER_ROW',
  PLATE_MAP: 'PLATE_MAP',
};

export const PLATE_MAP_RAW_TEXT_NAME = 'plate_map_raw_text.csv';

export const TRANSFER_MODAL_PANES = [
  MODAL_PANE.CHOOSE_TRANSFER_INPUT,
  MODAL_PANE.CHOOSE_TRANSFER_OUTPUT,
  MODAL_PANE.CONFIRM_TRANSFER_QUANTITIES,
  MODAL_PANE.CONFIRM_CONCENTRATIONS,
  MODAL_PANE.CONFIRM_SAMPLE_CONTROLS,
];

export type TransferModalPane =
  | ModalPane.CHOOSE_TRANSFER_INPUT
  | ModalPane.CHOOSE_TRANSFER_OUTPUT
  | ModalPane.CONFIRM_TRANSFER_QUANTITIES
  | ModalPane.CONFIRM_CONCENTRATIONS
  | ModalPane.CONFIRM_SAMPLE_CONTROLS;

export const MODAL_PANE_TO_TITLE = {
  [MODAL_PANE.CONFIRM_CONCENTRATIONS]: 'Confirm concentrations',
  [MODAL_PANE.CREATE_BOX]: 'Create box',
  [MODAL_PANE.CREATE_CONTAINERS]: 'Create containers',
  [MODAL_PANE.CREATE_LOCATION]: 'Create location',
  [MODAL_PANE.CREATE_PLATE]: 'Create plate',
  [MODAL_PANE.EXISTING_PLATE]: 'Find existing plate',
};

export interface SampleFile {
  id: string;
  iconId: string;
  name: string;
  result?: object; // Deprecated and does not exist on any deploys
  tags: Array<Tag>;
  tagSchema: TagSchema;
  residues?: string | null;
  schemaValidationState?: SchemaValidationState | null;
  url: string;
}

export const SAMPLE_FILE_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  result: PropTypes.object,
  tags: PropTypes.arrayOf(FileConstants.TAG_SHAPE),
  tagSchema: PropTypes.shape({
    iconId: PropTypes.string.isRequired,
    showResidues: PropTypes.bool.isRequired,
  }),
  residues: PropTypes.string,
});

// Keep in sync with batch.py to_dict(include_file=True)
export interface Batch {
  archiveRecord: ArchiveConstants.ArchiveRecord | null;
  batchSchemaId: string;
  concentration: Concentration;
  createdAt: string;
  creator: User;
  file: SampleFile;
  id: string;
  migratedToBasicFolderItemId: string | null;
  modifiedAt: string;
  name: string;
  permissions: Permissions;
  tags: Array<FileConstants.SchemaTag>;
  url: string;
}

export const BATCH_SHAPE = PropTypes.shape({
  archiveRecord: ArchiveConstants.ARCHIVE_RECORD_SHAPE,
  batchSchemaId: PropTypes.string.isRequired,
  createdAt: PropTypes.string,
  creator: USER_SHAPE.isRequired,
  file: SAMPLE_FILE_SHAPE,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  tags: PropTypes.arrayOf(FileConstants.TAG_SHAPE).isRequired,
  url: PropTypes.string.isRequired,
});

export interface BatchSummary {
  id: string;
  name: string;
}

export const BATCH_SUMMARY_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export interface ContainerContent {
  id: number;
  // Deprecated: use containedItemId instead.
  batchId?: string | null;
  containedItemId: string;
  containedItemTagSchemaId: string;
  containerId: string;
  concentration: Concentration;
}

export const CONTAINER_CONTENT_SHAPE = PropTypes.shape({
  containedItemId: PropTypes.string.isRequired,
  containedItemTagSchemaId: PropTypes.string.isRequired,
  containerId: PropTypes.string.isRequired,
  concentration: MEASUREMENT_SHAPE.isRequired,
});

export type ContainableSummary = Batch | FileConstants.BioentitySummary;

export function tagSchemaHasNoContainableType(schema: TagSchema): boolean {
  return !schema.isContainable && schema.batchSchemaId == null;
}

export const CONTAINABLE_SUMMARY_SHAPE = PropTypes.oneOfType([
  FileConstants.FILE_SUMMARY_SHAPE,
  BATCH_SUMMARY_SHAPE,
]);

export interface BatchLinkField {
  id: number;
  fieldType: string;
  name?: string;
  requiredLink: {
    batchSchema?: BatchSchema;
    batchSchemaGraph?: object;
  };
}

export const BATCH_LINK_FIELD_SHAPE = PropTypes.shape({
  id: PropTypes.number.isRequired,
  fieldType: PropTypes.string.isRequired,
  name: PropTypes.string,
  requiredLink: PropTypes.shape({
    batchSchema: PropTypes.shape({
      id: PropTypes.string.isRequired,
      fileTypeName: PropTypes.string.isRequired,
    }),
    batchSchemaGraph: PropTypes.object,
  }).isRequired,
});

export const BATCH_LINK_TAG_SHAPE = PropTypes.shape({
  fileId: PropTypes.string.isRequired,
  iconId: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

export enum BatchSchemaNamingScheme {
  BIOENTITY_NAME = 'BIOENTITY_NAME',
  BIOENTITY_REGISTRY_ID = 'BIOENTITY_REGISTRY_ID',
  BIOENTITY_REGISTRY_ID_WITH_DATE = 'BIOENTITY_REGISTRY_ID_WITH_DATE',
}

// Keep in sync with `BatchSchema.NamingScheme` in `batch_schema.py`
export const BATCH_SCHEMA_NAMING_SCHEMES = {
  BIOENTITY_REGISTRY_ID: 'BIOENTITY_REGISTRY_ID',
  BIOENTITY_REGISTRY_ID_WITH_DATE: 'BIOENTITY_REGISTRY_ID_WITH_DATE',
  BIOENTITY_NAME: 'BIOENTITY_NAME',
};

export interface BatchSchema extends Schema {
  fileTypeName: string;
  namingScheme: BatchSchemaNamingScheme;
  sqlIdentifier: string;
  tagSchemaId: string;
  hidden: boolean;
}

export const BATCH_SCHEMA_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(SchemaFieldConstants.SCHEMA_FIELD_SHAPE.isRequired).isRequired,
  allFields: PropTypes.arrayOf(SchemaFieldConstants.SCHEMA_FIELD_SHAPE.isRequired),
  fileTypeName: PropTypes.string,
  namingScheme: PropTypes.oneOf(Object.keys(BATCH_SCHEMA_NAMING_SCHEMES)).isRequired,
  sqlIdentifier: PropTypes.string,
  tagSchemaId: PropTypes.string.isRequired,
  archiveRecord: ArchiveConstants.ARCHIVE_RECORD_SHAPE,
  hidden: PropTypes.bool,
});

interface BioentitySchemaBatchLinkProps {
  entityToBatchLink?: boolean;
}

const BIOENTITY_SCHEMA_BATCH_LINK_PROPS_SHAPE = PropTypes.shape({
  entityToBatchLink: PropTypes.bool,
});

export interface BatchSchemaBatchLinkProps {
  pathsByTargetBatch: {[batchSchemaId: string]: Array<Array<SchemaFieldEdge>>};
  targetBatchSchemasById: {[batchSchemaId: string]: BatchSchema};
  sourceTagSchemaId: string;
}

const BATCH_SCHEMA_BATCH_LINK_PROPS_SHAPE = PropTypes.shape({
  pathsByTargetBatch: PropTypes.objectOf(PropTypes.arrayOf(SchemaFieldConstants.TAG_SCHEMA_PATH.isRequired))
    .isRequired,
  targetBatchSchemasById: PropTypes.objectOf(PropTypes.object).isRequired,
  sourceTagSchemaId: PropTypes.string.isRequired,
});

export type BatchLinkProps = BioentitySchemaBatchLinkProps | BatchSchemaBatchLinkProps;

/* BATCH_LINK_PROPS_SHAPE is a set of attributes about a batch that gets passed down as props between
 * components. It can either be for an entity -> batch link or a batch -> batch link, which have different
 * properties. */
export const BATCH_LINK_PROPS_SHAPE = PropTypes.oneOfType([
  BIOENTITY_SCHEMA_BATCH_LINK_PROPS_SHAPE,
  BATCH_SCHEMA_BATCH_LINK_PROPS_SHAPE,
]);

export interface BatchSchemaFilters {
  options: Array<BatchSchema>;
  value?: BatchSchema;
}

export const BATCH_SCHEMA_FILTERS_SHAPE = PropTypes.shape({
  options: PropTypes.arrayOf(BATCH_SCHEMA_SHAPE).isRequired,
  value: BATCH_SCHEMA_SHAPE,
});

export interface ParentStorableData {
  // nullable depending on ENABLE_REQUIRED_PARENT_STORABLE and whether the storables are locations
  id: string | null;
  gridCellColumnIndex: number | null;
  gridCellRowIndex: number | null;
}

export interface StorableSummary {
  archiveRecord: ArchiveConstants.ArchiveRecord | null;
  barcode: Optional<string>;
  checkOutStatusInfo?: CheckOutStatusInfo;
  createdAt: string;
  modifiedAt: string;
  id: string;
  name: string;
  parentStorableId?: Optional<string>;
  restrictionStatus?: SampleRestrictionStatus;
  sampleRoles?: SampleRoles;
  source: SourceSummary;
  starred: boolean;
  storableSchema: StorableSchema;
  tags: Array<FileConstants.SchemaTag>;
}

export interface MinimalStorableSummaryWithSchemaId extends Pick<StorableSummary, 'id' | 'name'> {
  storableSchema: Pick<StorableSchema, 'id'>;
}

export interface ValidationStateInfo {
  id: string;
  validationState?: {
    status: AsyncValidationStatus;
    validationErrorRecords: Array<SchemaValidationErrorRecord> | null;
  } | null;
}

const STORABLE_SUMMARY_PROPS = {
  archiveRecord: ArchiveConstants.ARCHIVE_RECORD_SHAPE,
  barcode: PropTypes.string,
  createdAt: PropTypes.string,
  modifiedAt: PropTypes.string,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  parentStorableId: PropTypes.string,
  restrictionStatus: PropTypes.oneOf(Object.values(SampleRestrictionStatus)),
  sampleRoles: SAMPLE_ROLES_SHAPE,
  starred: PropTypes.bool.isRequired,
  storableSchema: STORABLE_SCHEMA_SHAPE.isRequired,
  tags: PropTypes.arrayOf(FileConstants.TAG_SHAPE).isRequired,
};

export const STORABLE_SUMMARY_SHAPE = PropTypes.shape(STORABLE_SUMMARY_PROPS);

export interface StorableWithPermissions extends StorableSummary {
  creator: User;
  permissions: Permissions;
}

export interface Storable extends StorableWithPermissions {
  sourceId: string;
}

const STORABLE_PROPS = {
  ...STORABLE_SUMMARY_PROPS,
  permissions: PERMISSIONS_SHAPE.isRequired,
  sourceId: PropTypes.string.isRequired,
};

export const STORABLE_SUMMARY_WITH_PERMISSIONS_SHAPE = PropTypes.shape({
  ...STORABLE_SUMMARY_PROPS,
  permissions: PERMISSIONS_SHAPE.isRequired,
});

export const STORABLE_SHAPE = PropTypes.shape(STORABLE_PROPS);

export type TransferContainerSummary = ContainerSummary & {gridCellCoordinates?: string | null};

export type InitialParentStorable = BoxSummaryGraphQL | PlateSummaryGraphQL | LocationSummaryGraphQL;

export interface Location extends Storable {
  url: string;
}
export interface LocationSummary extends StorableSummary {
  url: string;
}

export interface LocationAncestry extends LocationSummary {
  parent: LocationAncestry | null;
}

export interface ListedLocation extends Location {
  parent: LocationAncestry | null;
}

export const LOCATION_SHAPE = PropTypes.shape({...STORABLE_PROPS, url: PropTypes.string.isRequired});
export const LOCATION_SUMMARY_SHAPE = PropTypes.shape({
  ...STORABLE_SUMMARY_PROPS,
  url: PropTypes.string.isRequired,
});

export interface Grid extends Storable {
  cells: Array<Array<GridCell | null>>;
  storableSchema: GridSchema;
}

/**
 * Grid type for when all cells are known to be accessible.
 *
 * Applicable for well plates, since their wells don't have separate permissions, or after another grid has
 * been checked with `isFullyAccessibleGrid`. Useful for when all cells will be acted on at once.
 */
export interface FullyAccessibleGrid extends Storable {
  cells: Array<Array<AccessibleGridCell | null>>;
  storableSchema: GridSchema;
}

export interface FullyAccessiblePlateGraphQL extends PlateGraphQL {
  cells: Array<Array<AccessibleGridCellGraphQL | null>>;
}

export interface FullyAccessibleBoxGraphQL extends BoxGraphQL {
  cells: Array<Array<AccessibleGridCellGraphQL | null>>;
}

export type FullyAccessibleGridGraphQL = FullyAccessiblePlateGraphQL | FullyAccessibleBoxGraphQL;

export function isFullyAccessibleGrid(grid?: Grid | null): grid is FullyAccessibleGrid {
  return (
    grid != null &&
    grid.cells.every((cellRow) => cellRow.every((cell) => cell == null || isAccessibleGridCell(cell)))
  );
}

export function isFullyAccessibleGridGraphQL(grid?: GridGraphQL | null): grid is FullyAccessibleGridGraphQL {
  return (
    grid != null &&
    grid.cells.every((cellRow) => cellRow.every((cell) => cell == null || isAccessibleGridCellGraphQL(cell)))
  );
}

export interface GridWithMinimalCells extends Storable {
  cells: Array<Array<MinimalContainerGridCell | null>>;
  storableSchema: GridSchema;
}

export const GRID_SHAPE = PropTypes.shape({
  ...STORABLE_PROPS,
  modifiedAt: PropTypes.string.isRequired,
  storableSchema: GRID_SCHEMA_SHAPE.isRequired,
});

export interface Plate extends Grid {
  storableSchema: PlateSchema;
}

export type Box = Grid;

export interface GridCellOverride {
  innerClassName?: string;
  tooltipTitle?: string;
  dataTestAttrs?: DataTestAttrs;
}

export const GRID_CELL_OVERRIDE_SHAPE = PropTypes.shape({
  innerClassName: PropTypes.string,
  tooltipTitle: PropTypes.string,
});

export interface GridSummary extends StorableSummary {
  storableSchema: GridSchema;
}

export type GridSummaryGraphQL = PlateSummaryGraphQL | BoxSummaryGraphQL;

export const GRID_SUMMARY_SHAPE = PropTypes.shape(STORABLE_SUMMARY_PROPS);

export interface GridCellSummary {
  columnIndex: number;
  rowIndex: number;
}

const GRID_CELL_SUMMARY_PROPS = {
  columnIndex: PropTypes.number.isRequired,
  rowIndex: PropTypes.number.isRequired,
};

export const GRID_CELL_SUMMARY_SHAPE = PropTypes.shape(GRID_CELL_SUMMARY_PROPS);

export interface ContainerSummary extends StorableSummary {
  url: string;
  volume?: LegacyMeasurement;
  quantity: LegacyMeasurement;
  restrictionStatus: SampleRestrictionStatus;
  isFixedPlateWell: boolean;
  role: ExperimentalRole | null;
}

const CONTAINER_SUMMARY_PROPS = {
  ...STORABLE_SUMMARY_PROPS,
  url: PropTypes.string.isRequired,
  volume: MEASUREMENT_SHAPE,
  quantity: MEASUREMENT_SHAPE,
  isFixedPlateWell: PropTypes.bool.isRequired,
};

export const CONTAINER_SUMMARY_SHAPE = PropTypes.shape(CONTAINER_SUMMARY_PROPS);

export interface Container extends Storable, ContainerSummary {
  // TODO(T8705): Batch->Container relationship is deprecated.
  batch?: Batch;
  contents: Array<ContainerContent>;
  containerSchemaId: string;
  creator: User;
  gridCell?: GridCellSummary;
  restrictionStatus: SampleRestrictionStatus;
  storableSchema: ContainerSchema;
}
const CONTAINER_PROPS = {
  ...STORABLE_PROPS,
  // TODO(T8705): Batch->Container relationship is deprecated.
  batch: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  contents: PropTypes.arrayOf(CONTAINER_CONTENT_SHAPE),
  containerSchemaId: PropTypes.string.isRequired,
  creator: USER_SHAPE,
  gridCell: GRID_CELL_SUMMARY_SHAPE,
  storableSchema: CONTAINER_SCHEMA_SHAPE,
};

export const CONTAINER_SHAPE = PropTypes.shape(CONTAINER_PROPS);

export type GridCellOrEmptyGridCell = GridCell | EmptyGridCell;
export interface EmptyGridCell extends GridCellSummary {
  container: null;
}

export interface InaccessibleStorable {
  invalidId: string;
}
export function isInaccessibleStorable(
  storable: InaccessibleStorable | SampleStatusItem
): storable is InaccessibleStorable {
  return (storable as InaccessibleStorable).invalidId != null;
}

export function getAccessibleOrInaccessibleStorableId(
  storable: InaccessibleStorable | SampleStatusItem
): string {
  return isInaccessibleStorable(storable) ? storable.invalidId : storable.id;
}
// Minimal types needed for rendering containers in a grid -- we want sample status info to check for errors
// and barcode/contents/id for basic rendering
export type MinimalContainer = SampleStatusItem & {
  barcode: Optional<string>;
  contents: Array<{containedItemId: string}>;
};
export interface MinimalContainerGridCell extends GridCellSummary {
  container: MinimalContainer | InaccessibleStorable;
}

export interface GridCell extends GridCellSummary {
  container: Container | InaccessibleStorable;
}

export interface AccessibleGridCell extends GridCellSummary {
  container: Container;
}

type GridCellGraphQL = NonNullable<GridGraphQL['cells'][number][number]>;

export type AccessibleGridCellGraphQL = GridCellGraphQL & {
  container: ContainerGraphQL;
};

export function isAccessibleGridCell(gridCell?: GridCellSummary | null): gridCell is AccessibleGridCell {
  return (
    (gridCell as GridCell)?.container != null && !isInaccessibleStorable((gridCell as GridCell).container)
  );
}

export function isAccessibleGridCellGraphQL(
  gridCell?: GridCellGraphQL | null
): gridCell is AccessibleGridCellGraphQL {
  return gridCell != null && !isInaccessible(gridCell.container);
}

export const GRID_CELL_SHAPE = PropTypes.shape({
  ...GRID_CELL_SUMMARY_PROPS,
  container: CONTAINER_SHAPE.isRequired,
});

export interface TextInfo {
  id: string;
  name: string | null;
  value: string | null;
}

export interface NumberInfo {
  id: string;
  name: string | null;
  isFloat: boolean;
  value: number | null;
}

export interface BooleanInfo {
  id: string;
  name: string | null;
  value: boolean | null;
}

export interface ContainerInfo {
  id: string;
  name: string;
  barcode?: Optional<string>;
  gridCell?: GridCellSummary | null;
}

export const CONTAINER_INFO_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  barcode: PropTypes.string,
  gridCell: GRID_CELL_SUMMARY_SHAPE,
});

export interface GridInfo {
  id: string;
  name: string;
  barcode: Optional<string>;
}

export interface BatchDescendantCounts {
  numNonWellContainers: number;
  numPlates: number;
}

export const BATCH_DESCENDANT_COUNTS_SHAPE = PropTypes.shape({
  numNonWellContainers: PropTypes.number.isRequired,
  numPlates: PropTypes.number.isRequired,
});

export interface BatchDescendants {
  nonWellContainers: Array<ContainerSummary>;
  plates: Array<GridSummary>;
}

export const BATCH_DESCENDANTS_SHAPE = PropTypes.shape({
  nonWellContainers: PropTypes.arrayOf(CONTAINER_SUMMARY_SHAPE).isRequired,
  plates: PropTypes.arrayOf(GRID_SUMMARY_SHAPE).isRequired,
});

export interface SelectedParentStorable {
  storable: StorableSummaryGraphQL | null;
  gridCells: Array<GridCellSummary>;
}

export enum SelectGridMode {
  EXISTING = 'EXISTING',
  CREATE = 'CREATE',
  CHOOSE = 'CHOOSE',
}

export const SELECTED_PARENT_STORABLE_SHAPE = PropTypes.shape({
  storable: STORABLE_SUMMARY_SHAPE,
  gridCells: PropTypes.arrayOf(PropTypes.shape(GRID_CELL_SUMMARY_PROPS)),
});

export const CONFIRM_TRANSFER_QUANTITIES_DATA_SHAPE = PropTypes.arrayOf(
  PropTypes.shape({
    outputContainer: CONTAINER_SHAPE.isRequired,
    transferQuantityValue: PropTypes.number,
    finalOutputQuantityValue: PropTypes.number,
  })
);

export interface ContainerTransferInfo {
  inputStorableIds: Array<string>;
  outputStorableIds: Array<string>;
}

export interface HydratedContainerTransferInfo {
  inputStorables: Array<StorableSummary | null>;
  outputStorables: Array<StorableSummary | null>;
}

export interface ContainerTransfer {
  id?: string;
  inputSampleId: string | null;
  outputContainer: ContainerSummary;
  volume?: LegacyMeasurement;
  quantity: LegacyMeasurement;
}

export interface ResponseContainerTransfer extends ContainerTransfer {
  inputContainer: ContainerSummary | null;
}

export const CONTAINER_TRANSFER_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  inputSampleId: PropTypes.string,
  outputContainer: CONTAINER_SUMMARY_SHAPE.isRequired,
});

export interface LabelTemplate {
  id: string;
  name: string;
  registryId: string;
  zplTemplate: string;
}

export const LABEL_TEMPLATE_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  registryId: PropTypes.string.isRequired,
  zplTemplate: PropTypes.string.isRequired,
});

export interface Printer {
  id: string;
  name: string;
  address: string;
  port?: number;
  description?: string;
  registryId: string;
}

export type PrinterForCreate = Omit<Printer, 'id' | 'registryId'>;

export interface PrinterServerResponse {
  printer: Printer;
}

export const PRINTER_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  address: PropTypes.string.isRequired,
  port: PropTypes.number,
  description: PropTypes.string,
  registryId: PropTypes.string.isRequired,
});

export const VALIDATION_ERRORS_SHAPE = PropTypes.shape({
  barcodeError: PropTypes.string,
  concentrationError: PropTypes.string,
  containerSchemaError: PropTypes.string,
  formError: PropTypes.string,
  numToCreateError: PropTypes.string,
  parentStorableError: PropTypes.string,
  parentStorableGridError: PropTypes.string,
  projectError: PropTypes.string,
  quantityError: PropTypes.string,
  schemaError: PropTypes.string,
});

export interface ValidationErrors {
  barcodeError?: string;
  concentrationError?: string;
  containerSchemaError?: string;
  contentsError?: string;
  formError?: string;
  numToCreateError?: string;
  parentStorableError?: string;
  parentStorableGridError?: string;
  projectError?: string;
  quantityError?: string;
  schemaError?: string;
  tagsError?: string;
  // Error message is already handled by the capacity input; simply need to block creation
  hasLocationCapacityError?: boolean;
}

export interface ChildContainerStatus {
  numFilledContainers: number;
  numTotalContainers: number;
}

export interface ChildStorableSchemasByLocationId {
  [locationId: string]: Array<{
    childStorableSchema: StorableSchemaSummary;
    numChildStorables: number;
  }>;
}

export interface LocationCapacityInfo {
  allowsNonLocationChildren: boolean;
  occupiedCapacity: number | null;
  totalCapacity: number | null;
  validChildSchemaIds: Array<string> | null;
}

export interface PlateSummaryExtras {
  viewableWellCountByPlateId: {[plateId: string]: number};
}

// TODO(TypeScript): This is initialized to {}, but aside from that, all properties are present.
// It should probably be changed to be null/optional and have all fields required.
export interface StorableSummaryExtras {
  ancestralStorablesById?: {[storableId: string]: Array<StorableSummary>}; // Deprecated
  ancestralStorableIdsByStorableId?: {[storableId: string]: Array<string>};
  childContainerParentFileSchemasByGridId?: {[gridId: string]: Array<TagSchemaSummary>};
  childContainerStatusesByGridId?: {
    [gridId: string]: ChildContainerStatus;
  };
  childStorableSchemasByLocationId?: ChildStorableSchemasByLocationId;
  gridCellsByContainerId?: {[containerId: string]: GridCellSummary | null};
  containedItemsByContainerId?: {[containerId: string]: Array<ContainableSummary>};
  containableConcentrationsByContainerId?: {
    [containerId: string]: {[containableId: string]: LegacyMeasurement};
  };
  locationCapacityById?: {
    [locationId: string]: LocationCapacityInfo;
  };
}

export const STORABLE_SUMMARY_EXTRAS_SHAPE = PropTypes.shape({
  ancestralStorablesById: PropTypes.objectOf(PropTypes.arrayOf(STORABLE_SUMMARY_SHAPE)), // Deprecated
  ancestralStorableIdsByStorableId: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  childContainerParentFileSchemasByGridId: PropTypes.object,
  childContainerStatusesByGridId: PropTypes.object,
  childStorableSchemasByLocationId: PropTypes.object,
  gridCellsByContainerId: PropTypes.objectOf(GRID_CELL_SUMMARY_SHAPE),
  containedItemsByContainerId: PropTypes.objectOf(PropTypes.arrayOf(CONTAINABLE_SUMMARY_SHAPE)),
});

export const GRID_SCHEMA_CLASSES = [ModelTypeConstants.BOX_SCHEMA, ModelTypeConstants.PLATE_SCHEMA];

export enum GridSchemaClass {
  BOX_SCHEMA = ModelTypeConstants.BOX_SCHEMA,
  PLATE_SCHEMA = ModelTypeConstants.PLATE_SCHEMA,
}

export const GRID_SCHEMA_CLASS_TO_TYPE_TEXT = {
  [ModelTypeConstants.BOX_SCHEMA]: 'Box',
  [ModelTypeConstants.PLATE_SCHEMA]: 'Plate',
};

export enum GridType {
  BOX = 'box',
  PLATE = 'plate',
}

export const GRID_CLASSES: Array<GridType> = [GridType.BOX, GridType.PLATE];

export const STORABLE_TYPES = [
  ModelTypeConstants.CONTAINER,
  ModelTypeConstants.BOX,
  ModelTypeConstants.PLATE,
  ModelTypeConstants.LOCATION,
] as const;

export const STORABLE_SCHEMA_TYPES = [
  ModelTypeConstants.CONTAINER_SCHEMA,
  ModelTypeConstants.BOX_SCHEMA,
  ModelTypeConstants.PLATE_SCHEMA,
  ModelTypeConstants.LOCATION_SCHEMA,
] as const;

export const STORABLE_SCHEMA_TYPE_TO_STORABLE_TYPE: {[key: string]: string} = {
  [ModelTypeConstants.BOX_SCHEMA]: ModelTypeConstants.BOX,
  [ModelTypeConstants.CONTAINER_SCHEMA]: ModelTypeConstants.CONTAINER,
  [ModelTypeConstants.LOCATION_SCHEMA]: ModelTypeConstants.LOCATION,
  [ModelTypeConstants.PLATE_SCHEMA]: ModelTypeConstants.PLATE,
};

// Keep in sync with `Storable.valid_parent_storable_classes`
export const STORABLE_CLASS_TO_VALID_PARENT_CLASSES: {
  [key: string]: Array<StorableItemType>;
} = {
  [ModelTypeConstants.CONTAINER]: [
    ModelTypeConstants.BOX,
    ModelTypeConstants.PLATE,
    ModelTypeConstants.LOCATION,
  ],
  [ModelTypeConstants.BOX]: [ModelTypeConstants.LOCATION],
  [ModelTypeConstants.PLATE]: [ModelTypeConstants.LOCATION],
  [ModelTypeConstants.LOCATION]: [ModelTypeConstants.LOCATION],
};

// This is a "normal" sample -- e.g., a batch or bioentity or container

export interface BaseSampleDetails {
  // batchInfo and bioentityInfo both null means that the user doesn't have permissions on the sample
  batchInfo: FileConstants.BatchInfo | null;
  bioentityInfo: FileConstants.BioentityInfo | null;
  booleanInfo: BooleanInfo | null;
  boxInfo: GridInfo | null;
  containerInfo: ContainerInfo | null;
  numberInfo: NumberInfo | null;
  plateInfo: GridInfo | null;
  textInfo: TextInfo | null;
  error: Optional<EvaluatedCellError>;
}

// Sets all values of BaseSampleDetails to null except for K, which is non-nullable, and 'error' which is either null or an EvaluatedCellError
type SpecificSampleDetails<K extends keyof BaseSampleDetails> = {
  [P in keyof BaseSampleDetails]: P extends K | 'error'
    ? P extends K
      ? NonNullable<BaseSampleDetails[P]>
      : BaseSampleDetails[P]
    : null;
};

type BioentitySampleDetails = SpecificSampleDetails<'bioentityInfo'>;
type ContainerSampleDetails = SpecificSampleDetails<'containerInfo'>;
type NumberSampleDetails = SpecificSampleDetails<'numberInfo'>;
type TextSampleDetails = SpecificSampleDetails<'textInfo'>;
type BooleanSampleDetails = SpecificSampleDetails<'booleanInfo'>;

// This version of SampleDetails represents a plate as a whole. It is used when propagating plates
// across runs or tables
type PlateSampleDetails = SpecificSampleDetails<'plateInfo'>;

type BoxSampleDetails = SpecificSampleDetails<'boxInfo'>;

// This version of SampleDetails represents a WELL, which is {plate!, position!}
export interface WellSampleDetails extends PlateSampleDetails {
  /**
   * We use a string to represent the position when we don't have details about the plate grid size,
   * which would require an asynchronous GraphQL call.
   */
  position: GridCellSummary | string;
}

export function isBioentitySample(sample: SampleDetails): sample is BioentitySampleDetails {
  return sample.bioentityInfo != null;
}

export function isContainerSample(sample: SampleDetails): sample is ContainerSampleDetails {
  return sample.containerInfo != null;
}

export function isWellSample(sample: SampleDetails): sample is WellSampleDetails {
  return (sample as WellSampleDetails).position != null;
}

export type SampleDetails =
  | BaseSampleDetails
  | BoxSampleDetails
  | PlateSampleDetails
  | WellSampleDetails
  | TextSampleDetails
  | NumberSampleDetails
  | BooleanSampleDetails;

interface WithSchemaExtension {
  schema: TagSchema | BatchSchema | null;
}

export type SampleDetailsWithSchema = SampleDetails & WithSchemaExtension;
export interface StorableModalCreateProps {
  name: string;
  barcode: string;
  selectedSchemaId: string;
  sourceId: string;
  parentStorableData: ParentStorableData;
  tags: Array<Tag>;
}

interface PartialContainerContent {
  __typename: 'ContainerContentNode';
  containedItem: {__typename: string};
}

export function getNonBatchContainerContents<
  T extends PartialContainerContent,
  R extends PartialContainerContent,
>(contents: Array<T>): Array<R> {
  return contents.filter(
    (content) => content.containedItem.__typename !== 'BatchNode'
  ) as unknown as Array<R>;
}

export function getAccessibleNonBatchContainerContents<
  T extends PartialContainerContent,
  R extends PartialContainerContent,
>(contents: Array<T>): Array<R> {
  return contents.filter(
    (content) =>
      content.containedItem.__typename !== 'BatchNode' &&
      content.containedItem.__typename !== 'InaccessibleInfoNode'
  ) as unknown as Array<R>;
}
